<template>
  <main class="claim-page afro-bg">
    <section class="claim-shell">
      <header class="claim-hero paper-panel">
        <img src="/assets/afrogato/logo-afrogato.png" alt="Afrogato Café" class="claim-logo">
        <div>
          <p class="eyebrow">Afrogato Rewards</p>
          <h1>Tu compra también se convierte en recompensas</h1>
          <p class="hero-copy">Cada $10 de compra suma 1 punto y cada punto vale $1 al canjear productos participantes.</p>
        </div>
      </header>

      <section class="steps-grid" aria-label="Cómo funcionan los puntos">
        <article class="paper-panel"><b>1</b><h2>Compra</h2><p>Guarda el ticket que recibiste en caja.</p></article>
        <article class="paper-panel"><b>2</b><h2>Reclama</h2><p>Escanea el QR e inicia sesión o crea tu cuenta aquí mismo.</p></article>
        <article class="paper-panel"><b>3</b><h2>Disfruta</h2><p>Usa tus puntos desde tu wallet para pedir recompensas.</p></article>
      </section>

      <section class="ticket-panel paper-panel" aria-live="polite">
        <div class="ticket-heading">
          <div>
            <p class="eyebrow">Este ticket</p>
            <h2>{{ ticketTitle }}</h2>
          </div>
          <span class="status-pill" :class="ticketStatusClass">{{ ticketStatusLabel }}</span>
        </div>

        <div v-if="previewLoading" class="state-box">Consultando tu ticket…</div>
        <div v-else-if="preview" class="ticket-summary">
          <div><span>Total</span><strong>{{ money(preview.ticket?.total || 0) }}</strong></div>
          <div><span>Puntos por recibir</span><strong>{{ formatPoints(preview.points_to_award || 0) }} pts</strong></div>
        </div>
        <div v-else-if="previewError" class="state-box warning">{{ previewError }}</div>

        <div v-if="claimState === 'claiming'" class="state-box">Estamos agregando los puntos a tu wallet…</div>
        <div v-else-if="claimState === 'success'" class="claim-result success-box">
          <span aria-hidden="true">✓</span>
          <div>
            <h3>{{ claimResult.idempotent ? 'Tus puntos ya estaban seguros' : 'Puntos agregados' }}</h3>
            <p>{{ claimResult.idempotent ? 'Este ticket ya estaba acreditado en tu cuenta.' : `Sumamos ${formatPoints(claimResult.claim?.points_awarded || 0)} puntos a tu cuenta.` }}</p>
            <strong>Saldo actual: {{ formatPoints(claimResult.points_balance || 0) }} puntos</strong>
          </div>
        </div>
        <div v-else-if="claimState === 'error'" class="state-box warning">
          <strong>No pudimos acreditar el ticket.</strong>
          <span>{{ claimError }}</span>
          <button v-if="isLoggedIn" type="button" class="small-btn" @click="claimNow">Intentar de nuevo</button>
        </div>

        <div v-if="!isLoggedIn && claimState !== 'success'" class="auth-block">
          <div class="auth-tabs" role="tablist">
            <button type="button" :class="{ active: authMode === 'login' }" @click="authMode = 'login'">Ya tengo cuenta</button>
            <button type="button" :class="{ active: authMode === 'register' }" @click="authMode = 'register'">Crear cuenta</button>
          </div>

          <form v-if="authMode === 'login'" class="auth-form" @submit.prevent="handleLogin">
            <h3>Entra y acredita este ticket</h3>
            <label><span>Correo o usuario</span><input v-model.trim="loginForm.email" type="text" autocomplete="username" required></label>
            <label><span>Contraseña</span><input v-model="loginForm.password" type="password" autocomplete="current-password" required></label>
            <p v-if="authError" class="form-error">{{ authError }}</p>
            <button type="submit" class="primary-btn" :disabled="authLoading">{{ authLoading ? 'Entrando…' : 'Entrar y sumar puntos' }}</button>
          </form>

          <form v-else class="auth-form" @submit.prevent="handleRegister">
            <h3>Crea tu wallet sin salir de aquí</h3>
            <label><span>Nombre</span><input v-model.trim="registerForm.nombre" type="text" autocomplete="name" required></label>
            <label><span>Correo</span><input v-model.trim="registerForm.email" type="email" autocomplete="email" required></label>
            <label><span>Teléfono</span><input :value="registerForm.telefono" type="tel" inputmode="numeric" autocomplete="tel" maxlength="10" required @input="onPhoneInput"></label>
            <div class="password-grid">
              <label><span>Contraseña</span><input v-model="registerForm.password" type="password" autocomplete="new-password" minlength="8" required></label>
              <label><span>Confirmar</span><input v-model="registerForm.confirmPassword" type="password" autocomplete="new-password" minlength="8" required></label>
            </div>
            <p v-if="authError" class="form-error">{{ authError }}</p>
            <button type="submit" class="primary-btn" :disabled="authLoading">{{ authLoading ? 'Creando cuenta…' : 'Crear cuenta y sumar puntos' }}</button>
          </form>
        </div>

        <div v-else-if="isLoggedIn && claimState === 'idle'" class="state-box">
          <button type="button" class="primary-btn" @click="claimNow">Sumar puntos a mi cuenta</button>
        </div>

        <div v-if="claimState === 'success'" class="result-actions">
          <router-link to="/wallet">Abrir mi wallet</router-link>
          <router-link to="/menu">Ver el menú</router-link>
        </div>
      </section>

      <section class="install-panel paper-panel">
        <div>
          <p class="eyebrow">Llévala contigo</p>
          <h2>Instala Afrogato como aplicación</h2>
          <p>No necesitas una tienda de aplicaciones. Así tendrás tu wallet, QR y recompensas a un toque.</p>
        </div>
        <button type="button" class="install-btn" @click="installApp">Instalar Afrogato</button>
        <p class="install-help">{{ installMessage || 'En Android o computadora también puedes usar el menú del navegador → “Instalar aplicación”. En iPhone: Compartir → “Agregar a pantalla de inicio”.' }}</p>
      </section>
    </section>
  </main>
</template>

<script>
import axios from 'axios'
import { mapActions } from 'vuex'
import { claimTicket, getTicketPreview } from '@/services/menuApi'
import { extractTicketUuid } from '@/utils/ticketCode'

export default {
  name: 'TicketClaimView',
  props: {
    ticketUuid: { type: String, default: '' }
  },
  data() {
    return {
      normalizedTicketUuid: '',
      previewLoading: true,
      preview: null,
      previewError: '',
      previewCode: '',
      claimState: 'idle',
      claimError: '',
      claimResult: {},
      authMode: 'login',
      authLoading: false,
      authError: '',
      loginForm: { email: '', password: '' },
      registerForm: { nombre: '', email: '', telefono: '', password: '', confirmPassword: '' },
      deferredInstallPrompt: null,
      installMessage: ''
    }
  },
  computed: {
    isLoggedIn() { return Boolean(this.$store.state.user?.token) },
    ticketTitle() { return this.preview?.ticket?.ticket_number || 'Reclama tus puntos' },
    ticketStatusLabel() {
      if (this.previewLoading) return 'Consultando'
      if (this.claimState === 'success') return 'Acreditado'
      if (this.preview) return 'Listo para reclamar'
      if (this.previewCode === 'ticket_already_claimed') return 'Ya reclamado'
      return 'Revisar ticket'
    },
    ticketStatusClass() {
      if (this.claimState === 'success') return 'success'
      if (this.preview) return 'ready'
      return 'warning'
    }
  },
  async mounted() {
    this.normalizedTicketUuid = extractTicketUuid(this.ticketUuid)
    window.addEventListener('beforeinstallprompt', this.captureInstallPrompt)
    window.addEventListener('appinstalled', this.onAppInstalled)
    if (!this.normalizedTicketUuid) {
      this.previewLoading = false
      this.previewError = 'El enlace no contiene un identificador de ticket válido.'
      this.claimState = 'error'
      this.claimError = this.previewError
      return
    }
    await this.loadPreview()
    if (this.isLoggedIn) await this.claimNow()
  },
  beforeUnmount() {
    window.removeEventListener('beforeinstallprompt', this.captureInstallPrompt)
    window.removeEventListener('appinstalled', this.onAppInstalled)
  },
  methods: {
    ...mapActions(['login']),
    async loadPreview() {
      this.previewLoading = true
      this.previewError = ''
      try {
        this.preview = await getTicketPreview(this.normalizedTicketUuid)
        this.previewCode = this.preview.code || 'ticket_valid'
      } catch (error) {
        const data = error.response?.data || {}
        this.previewCode = data.code || 'ticket_unavailable'
        this.previewError = data.error || 'No se pudo consultar el ticket en este momento.'
      } finally {
        this.previewLoading = false
      }
    },
    async claimNow() {
      if (!this.isLoggedIn || !this.normalizedTicketUuid || this.claimState === 'claiming') return
      this.claimState = 'claiming'
      this.claimError = ''
      try {
        this.claimResult = await claimTicket(this.normalizedTicketUuid)
        this.claimState = 'success'
      } catch (error) {
        this.claimError = error.response?.data?.error || 'No se pudo acreditar el ticket. Intenta nuevamente.'
        this.claimState = 'error'
      }
    },
    async handleLogin() {
      this.authError = ''
      if (!this.loginForm.email || !this.loginForm.password) { this.authError = 'Completa tu correo y contraseña.'; return }
      this.authLoading = true
      try {
        const { data } = await axios.post('/api/login_post', this.loginForm)
        if (!data?.success || !data.user) throw new Error(data?.error || 'Usuario o contraseña incorrectos.')
        await this.login(data.user)
        await this.claimNow()
      } catch (error) {
        this.authError = error.response?.data?.error || error.message || 'No se pudo iniciar sesión.'
      } finally {
        this.authLoading = false
      }
    },
    async handleRegister() {
      this.authError = this.validateRegistration()
      if (this.authError) return
      this.authLoading = true
      try {
        const { data } = await axios.post('/api/register', {
          nombre: this.registerForm.nombre,
          email: this.registerForm.email,
          telefono: this.registerForm.telefono,
          password: this.registerForm.password,
          password_confirm: this.registerForm.confirmPassword
        })
        if (!data?.success || !data.user) throw new Error(data?.error || 'No se pudo crear tu cuenta.')
        await this.login(data.user)
        await this.claimNow()
      } catch (error) {
        this.authError = error.response?.data?.error || error.message || 'No se pudo crear tu cuenta.'
      } finally {
        this.authLoading = false
      }
    },
    validateRegistration() {
      if (this.registerForm.nombre.length < 3) return 'Escribe tu nombre completo.'
      if (!/^\S+@\S+\.\S+$/.test(this.registerForm.email)) return 'Escribe un correo válido.'
      if (!/^\d{10}$/.test(this.registerForm.telefono)) return 'Escribe un teléfono de 10 dígitos.'
      if (this.registerForm.password.length < 8) return 'La contraseña debe tener al menos 8 caracteres.'
      if (this.registerForm.password !== this.registerForm.confirmPassword) return 'Las contraseñas no coinciden.'
      return ''
    },
    onPhoneInput(event) { this.registerForm.telefono = String(event.target.value || '').replace(/\D/g, '').slice(0, 10) },
    captureInstallPrompt(event) { event.preventDefault(); this.deferredInstallPrompt = event },
    onAppInstalled() { this.deferredInstallPrompt = null; this.installMessage = 'Afrogato quedó instalada en tu dispositivo.' },
    async installApp() {
      if (this.deferredInstallPrompt) {
        this.deferredInstallPrompt.prompt()
        const choice = await this.deferredInstallPrompt.userChoice
        this.installMessage = choice.outcome === 'accepted' ? 'Afrogato se está instalando.' : 'Puedes instalarla cuando quieras desde el menú del navegador.'
        this.deferredInstallPrompt = null
        return
      }
      const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent)
      this.installMessage = isIos
        ? 'En Safari toca Compartir y después “Agregar a pantalla de inicio”.'
        : 'Abre el menú de tu navegador y elige “Instalar aplicación” o “Agregar a pantalla de inicio”.'
    },
    formatPoints(value) { const number = Number(value || 0); return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0+$/, '').replace(/\.$/, '') },
    money(value) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value || 0)) }
  }
}
</script>

<style scoped>
.claim-page { min-height: 100vh; padding: clamp(24px, 5vw, 70px) 16px 70px; color: var(--afro-ink); }
.claim-shell { width: min(1080px, 100%); margin: 0 auto; display: grid; gap: 18px; }
.paper-panel { border-radius: 28px; background: rgba(243,223,189,.96); border: 1px solid rgba(38,51,31,.16); padding: clamp(20px, 4vw, 34px); box-shadow: 0 16px 44px rgba(0,0,0,.13); }
.claim-hero { display: grid; grid-template-columns: minmax(180px, 260px) 1fr; gap: clamp(20px, 5vw, 52px); align-items: center; overflow: hidden; }
.claim-logo { width: 100%; max-height: 220px; object-fit: contain; filter: drop-shadow(0 12px 18px rgba(0,0,0,.16)); }
.eyebrow { margin: 0 0 8px; color: #8a5a0c; font-weight: 1000; text-transform: uppercase; letter-spacing: .14em; }
h1, h2, h3 { font-family: var(--afro-text-serif); color: var(--afro-ink); }
h1 { margin: 0; font-size: clamp(38px, 6vw, 68px); line-height: .98; }
h2, h3 { margin: 0; }
.hero-copy { max-width: 680px; margin: 16px 0 0; font-weight: 800; line-height: 1.55; color: rgba(38,51,31,.76); }
.steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.steps-grid article { display: grid; grid-template-columns: 52px 1fr; gap: 4px 12px; align-items: center; padding: 20px; }
.steps-grid b { grid-row: 1 / 3; width: 48px; height: 48px; display: grid; place-items: center; border-radius: 50%; background: #26331f; color: #f3dfbd; font-size: 22px; }
.steps-grid p { margin: 2px 0 0; color: rgba(38,51,31,.7); font-weight: 750; }
.ticket-panel { display: grid; gap: 18px; }
.ticket-heading { display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; }
.ticket-heading h2 { font-size: clamp(34px, 5vw, 54px); }
.status-pill { border-radius: 999px; padding: 10px 14px; background: rgba(138,90,12,.12); color: #7a4b08; font-weight: 1000; white-space: nowrap; }
.status-pill.ready, .status-pill.success { background: #dcfce7; color: #166534; }
.ticket-summary { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.ticket-summary div { border-radius: 22px; padding: 18px; background: rgba(255,248,230,.68); border: 1px solid rgba(38,51,31,.12); display: grid; gap: 5px; }
.ticket-summary span { color: rgba(38,51,31,.66); font-weight: 850; }
.ticket-summary strong { font-size: clamp(25px, 5vw, 38px); }
.state-box { border-radius: 20px; padding: 16px; background: rgba(38,51,31,.08); font-weight: 850; display: grid; gap: 10px; }
.state-box.warning, .form-error { background: #fee2e2; color: #8b1d1d; border: 1px solid #fecaca; }
.success-box { display: grid; grid-template-columns: 58px 1fr; gap: 16px; align-items: center; border-radius: 24px; padding: 20px; background: #dcfce7; color: #166534; }
.success-box > span { width: 54px; height: 54px; display: grid; place-items: center; border-radius: 50%; background: #166534; color: white; font-size: 30px; }
.success-box p { margin: 5px 0; font-weight: 800; }
.auth-block { border-top: 1px dashed rgba(38,51,31,.25); padding-top: 18px; }
.auth-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 6px; border-radius: 18px; background: rgba(38,51,31,.08); }
.auth-tabs button { border: 0; border-radius: 14px; min-height: 46px; background: transparent; color: #26331f; font-weight: 1000; cursor: pointer; }
.auth-tabs button.active { background: #26331f; color: #f3dfbd; }
.auth-form { margin-top: 18px; display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }
.auth-form h3, .auth-form .primary-btn, .auth-form .form-error { grid-column: 1 / -1; }
.auth-form label { display: grid; gap: 6px; font-weight: 900; }
.auth-form input { width: 100%; min-height: 50px; border: 1px solid rgba(38,51,31,.22); border-radius: 15px; padding: 0 14px; background: rgba(255,248,230,.82); color: #26331f; font: inherit; outline: none; }
.auth-form input:focus { border-color: #8a5a0c; box-shadow: 0 0 0 3px rgba(138,90,12,.12); }
.password-grid { grid-column: 1 / -1; display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }
.form-error { grid-column: 1 / -1; border-radius: 14px; padding: 11px 13px; margin: 0; font-weight: 900; }
.primary-btn, .small-btn, .install-btn { border: 0; border-radius: 17px; min-height: 52px; padding: 12px 18px; background: linear-gradient(135deg, #26331f, #4a5d2c); color: #fff8e6; font-weight: 1000; cursor: pointer; }
.small-btn { justify-self: start; min-height: 42px; }
button:disabled { opacity: .55; cursor: wait; }
.result-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.result-actions a { border-radius: 999px; padding: 12px 18px; background: #26331f; color: #fff8e6; text-decoration: none; font-weight: 1000; }
.result-actions a:last-child { background: rgba(38,51,31,.1); color: #26331f; }
.install-panel { display: grid; grid-template-columns: 1fr auto; gap: 16px 24px; align-items: center; }
.install-panel h2 { font-size: clamp(30px, 5vw, 48px); }
.install-panel p { margin: 8px 0 0; max-width: 720px; color: rgba(38,51,31,.72); font-weight: 800; line-height: 1.5; }
.install-help { grid-column: 1 / -1; border-top: 1px dashed rgba(38,51,31,.22); padding-top: 12px; font-size: 14px; }
@media (max-width: 760px) {
  .claim-page { padding: 12px 10px 50px; }
  .claim-hero { grid-template-columns: 1fr; text-align: center; }
  .claim-logo { width: min(210px, 60vw); margin: 0 auto; }
  .steps-grid { grid-template-columns: 1fr; }
  .ticket-heading { display: grid; }
  .status-pill { justify-self: start; }
  .ticket-summary, .auth-form, .password-grid { grid-template-columns: 1fr; }
  .auth-form > *, .password-grid { grid-column: 1; }
  .install-panel { grid-template-columns: 1fr; }
  .install-btn { width: 100%; }
}
</style>
